import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import {
  CheckCircle2,
  CreditCard,
  ImagePlus,
  RefreshCw,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import axios from "axios";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const getApiErrorMessage = (error, fallback = "QRIS gagal diproses.") => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message;
    }

    if (typeof detail.msg === "string" && detail.msg.trim()) {
      return detail.msg;
    }
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item.msg === "string") return item.msg;
        if (item && typeof item.message === "string") return item.message;
        return "";
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(", ");
    }
  }

  if (
    typeof error?.response?.data?.message === "string" &&
    error.response.data.message.trim()
  ) {
    return error.response.data.message;
  }

  return fallback;
};

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];


export default function PlatformQRISPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [decodeStatus, setDecodeStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const fileInputRef = useRef(null);

  const loadQRIS = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get("/api/platform/qris");
      setData(response.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Unable to load subscription QRIS configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQRIS();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetUpload = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(null);
    setPreviewUrl("");
    setQrPayload("");
    setDecodeStatus("");
    setUploadError("");
    setUploadSuccess("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const decodeQRCode = (file) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = () => {
        try {
          const maxDimension = 1600;
          const scale = Math.min(
            1,
            maxDimension / Math.max(image.naturalWidth, image.naturalHeight)
          );

          const width = Math.max(1, Math.round(image.naturalWidth * scale));
          const height = Math.max(1, Math.round(image.naturalHeight * scale));

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const context = canvas.getContext("2d", {
            willReadFrequently: true,
          });

          if (!context) {
            reject(new Error("Unable to create image processing context."));
            return;
          }

          context.drawImage(image, 0, 0, width, height);

          const imageData = context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

          const result = jsQR(
            imageData.data,
            imageData.width,
            imageData.height,
            {
              inversionAttempts: "attemptBoth",
            }
          );

          if (!result?.data) {
            reject(
              new Error(
                "QR code tidak ditemukan. Pastikan gambar QRIS jelas dan seluruh QR code terlihat."
              )
            );
            return;
          }

          resolve(result.data);
        } catch (err) {
          reject(err);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Gambar QRIS tidak dapat dibaca."));
      };

      image.src = objectUrl;
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    setUploadError("");
    setUploadSuccess("");
    setQrPayload("");
    setDecodeStatus("");

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl("");
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setPreviewUrl("");
      setDecodeStatus("");
      setUploadError("File harus berupa PNG, JPG/JPEG, atau WEBP.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setSelectedFile(null);
      setPreviewUrl("");
      setDecodeStatus("");
      setUploadError("Ukuran file maksimal 2 MB.");
      event.target.value = "";
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const objectUrl = URL.createObjectURL(file);

    setSelectedFile(file);
    setPreviewUrl(objectUrl);
    setDecodeStatus("Membaca QRIS…");

    try {
      const payload = await decodeQRCode(file);

      setQrPayload(payload);
      setDecodeStatus("QRIS berhasil dibaca.");
    } catch (err) {
      setQrPayload("");
      setDecodeStatus("");
      setUploadError(
        err?.message ||
          "QRIS tidak dapat dibaca dari gambar. Gunakan gambar QRIS yang lebih jelas."
      );
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !qrPayload) {
      setUploadError("Pilih gambar QRIS yang berhasil dibaca terlebih dahulu.");
      return;
    }

    const replacing = Boolean(data?.active?.id);

    if (
      replacing &&
      !window.confirm(
        "QRIS subscription yang sedang aktif akan diganti. QRIS lama tetap tersimpan di history sebagai REPLACED. Lanjutkan?"
      )
    ) {
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const formData = new FormData();
      formData.append("qr_payload", qrPayload);
      formData.append("file", selectedFile);

      const endpoint = replacing
        ? `/api/platform/qris/${data.active.id}/replace`
        : "/api/platform/qris/upload";

      const response = await axios.post(endpoint, formData);

      setUploadSuccess(
        response?.data?.message ||
          (replacing
            ? "Subscription QRIS berhasil diganti."
            : "Subscription QRIS berhasil diaktifkan.")
      );

      resetUpload();
      await loadQRIS();
    } catch (err) {
      setUploadError(
        getApiErrorMessage(
          err,
          replacing
            ? "Subscription QRIS gagal diganti."
            : "QRIS gagal diaktifkan."
        )
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
            <ImagePlus className="text-primary" size={22} />
          </div>

          <div>
            <h1 className="text-2xl font-heading font-bold">
              QRIS
            </h1>

            <p className="text-sm text-muted-foreground">
              Manage the QRIS used for GLOO POS subscription payments.
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading QRIS configuration…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
          <p className="text-sm text-red-400">{error}</p>

          <button
            onClick={loadQRIS}
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-sm hover:bg-accent"
          >
            <RefreshCw size={15} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
                    {data.active ? (
            <div className="rounded-xl border border-border bg-card p-6 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="text-emerald-400" size={18} />
                    <span className="font-semibold">ACTIVE</span>
                  </div>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Current subscription payment QRIS.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Merchant</p>
                  <p className="font-medium">
                    {data.active.merchant_name || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">City</p>
                  <p className="font-medium">
                    {data.active.merchant_city || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Currency</p>
                  <p className="font-medium">
                    {data.active.currency || "IDR"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">QRIS ID</p>
                  <p className="font-mono text-xs break-all">
                    {data.active.id}
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-secondary/60 p-4">
                <p className="text-xs text-muted-foreground mb-2">
                  QRIS Payload
                </p>

                <p className="font-mono text-xs break-all leading-relaxed">
                  {data.active.qr_payload}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
              <CreditCard
                className="mx-auto mb-3 text-muted-foreground"
                size={28}
              />

              <h2 className="font-semibold">
                No active subscription QRIS
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                Add a valid QRIS before accepting subscription payments.
              </p>
            </div>
          )}

          {uploadSuccess && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 size={18} />
                <p className="text-sm font-medium">{uploadSuccess}</p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2">
                <ImagePlus size={18} className="text-primary" />

                <h2 className="font-semibold">
                  {data.active ? "Replace QRIS" : "Upload QRIS"}
                </h2>
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                Upload a clear QRIS image. The QR code will be decoded and
                validated before activation.
              </p>
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-secondary hover:bg-accent text-sm font-medium disabled:opacity-50"
              >
                <Upload size={16} />
                Choose QRIS Image
              </button>

              <p className="mt-2 text-xs text-muted-foreground">
                PNG, JPG/JPEG, or WEBP · maximum 2 MB
              </p>
            </div>

            {uploadError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                <p className="text-sm text-red-400">{uploadError}</p>
              </div>
            )}

            {selectedFile && previewUrl && (
              <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
                <div className="rounded-xl border border-border bg-secondary/30 p-4">
                  <p className="text-xs text-muted-foreground mb-3">
                    QRIS Image Preview
                  </p>

                  <div className="aspect-square rounded-lg bg-white flex items-center justify-center overflow-hidden">
                    <img
                      src={previewUrl}
                      alt="QRIS preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Selected file
                    </p>

                    <p className="text-sm font-medium break-all">
                      {selectedFile.name}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">
                      QRIS decoder
                    </p>

                    <p
                      className={`text-sm font-medium ${
                        qrPayload
                          ? "text-emerald-400"
                          : "text-muted-foreground"
                      }`}
                    >
                      {decodeStatus || "Waiting for QRIS scan…"}
                    </p>
                  </div>

                  {qrPayload && (
                    <div className="rounded-lg bg-secondary/60 p-4">
                      <p className="text-xs text-muted-foreground mb-2">
                        Decoded QRIS Payload
                      </p>

                      <p className="font-mono text-xs break-all leading-relaxed">
                        {qrPayload}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={resetUpload}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-secondary hover:bg-accent text-sm font-medium disabled:opacity-50"
                    >
                      <X size={16} />
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={!qrPayload || uploading}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-sm font-semibold disabled:opacity-50"
                    >
                      <ShieldCheck size={16} />

                      {uploading
                        ? "Activating…"
                        : data.active
                        ? "Replace & Activate"
                        : "Activate QRIS"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card">
            <div className="p-5 border-b border-border">
              <h2 className="font-semibold">QRIS History</h2>

              <p className="text-sm text-muted-foreground mt-1">
                Previous QRIS configurations are retained for audit and
                historical payment references.
              </p>
            </div>

            <div className="divide-y divide-border">
              {(data.history || []).length === 0 ? (
                <div className="p-5 text-sm text-muted-foreground">
                  No QRIS history yet.
                </div>
              ) : (
                data.history.map((item) => (
                  <div
                    key={item.id}
                    className="p-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">
                        {item.merchant_name || "Unknown merchant"}
                      </p>

                      <p className="text-xs text-muted-foreground">
                        {item.merchant_city || "—"} ·{" "}
                        {item.status || "UNKNOWN"}
                      </p>
                    </div>

                    <span className="text-xs font-mono text-muted-foreground">
                      {item.id}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
